import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

const navLinks = [
  { label: 'Servicios', href: '#servicios' },
  { label: 'Resultados', href: '#resultados' },
  { label: 'Contacto', href: '#contacto' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      data-testid="main-navigation"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/80 border-b border-r2r-border shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <a
          href="#hero"
          data-testid="nav-logo"
          onClick={(e) => { e.preventDefault(); handleNav('#hero'); }}
          className="flex items-center gap-3 group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/04suonqk_VIDEO%20%281%29.png"
            alt="Ready2Rent"
            className="h-10 lg:h-12 w-auto object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.label.toLowerCase()}`}
              onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
              className="font-manrope text-sm font-medium text-r2r-text-secondary hover:text-r2r-text transition-colors relative after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-[1px] after:bg-r2r-text after:transition-all hover:after:w-full"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Phone CTA */}
        <a
          href="tel:+56966052019"
          data-testid="nav-phone-button"
          className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-r2r-primary text-white font-manrope text-sm font-medium hover:bg-r2r-primary-hover transition-all hover:-translate-y-[1px]"
        >
          <Phone size={14} />
          <span>+56 9 6605 2019</span>
        </a>

        {/* Mobile Menu Toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden p-2 text-r2r-text"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-r2r-border overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                  className="font-manrope text-base font-medium text-r2r-text-secondary hover:text-r2r-text transition-colors py-2 border-b border-r2r-border"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="tel:+56966052019"
                data-testid="mobile-phone-button"
                className="flex items-center justify-center gap-2 mt-2 px-5 py-3 bg-r2r-primary text-white font-manrope text-sm font-medium"
              >
                <Phone size={14} />
                <span>+56 9 6605 2019</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
